/**
 * Admin Analytics Page
 *
 * Credit usage and API statistics with exportable reports
 */

import { useEffect, useState } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAdminStore, useAllCompanies, useAdminLoading } from '@/stores/adminStore';
import {
  Download,
  TrendingUp,
  Activity,
  Loader2,
} from 'lucide-react';

export function AdminAnalyticsPage() {
  const { fetchAllCompanies, exportReport } = useAdminStore();
  const companies = useAllCompanies();
  const isLoading = useAdminLoading();
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    Logger.info(LogCategory.UI, 'AdminAnalyticsPage mounted', 'AdminAnalyticsPage');
    fetchAllCompanies();
  }, [fetchAllCompanies]);

  const handleExport = async (type: 'credit_usage' | 'api_usage') => {
    setIsExporting(true);
    try {
      const blob = await exportReport(type);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to export report', 'handleExport', undefined, error);
    } finally {
      setIsExporting(false);
    }
  };

  // Separate internal and external accounts
  const externalCompanies = companies.filter(c => !c.isInternal);
  const internalCompanies = companies.filter(c => c.isInternal);

  // Calculate analytics (external accounts only)
  const totalCredits = externalCompanies.reduce((sum, c) => sum + c.credits, 0);
  const avgCredits = externalCompanies.length > 0 ? Math.round(totalCredits / externalCompanies.length) : 0;
  const lowCreditCustomers = externalCompanies.filter(c => c.credits < (c.settings?.lowCreditThreshold || 100));

  // Group by plan (external accounts only)
  const byPlan = externalCompanies.reduce((acc, c) => {
    acc[c.plan] = (acc[c.plan] || 0) + c.credits;
    return acc;
  }, {} as Record<string, number>);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Analytics & Reports</h2>
          <p className="text-slate-500 mt-1">Credit usage and API statistics</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-slate-500">Total Credits</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {totalCredits.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-slate-500">Avg Credits/Customer</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {avgCredits.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-slate-500">Low Credit Accounts</p>
                  <p className="text-2xl font-bold text-warning-600 mt-1">
                    {lowCreditCustomers.length}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-slate-500">External Customers</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {externalCompanies.length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Credits by Plan */}
            <Card>
              <CardHeader
                title="Credits by Plan"
                description="Distribution of credits across subscription tiers"
              />
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Enterprise</span>
                      <span className="text-sm font-bold text-slate-900">
                        {(byPlan['enterprise'] || 0).toLocaleString()} credits
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${totalCredits > 0 ? ((byPlan['enterprise'] || 0) / totalCredits) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Professional</span>
                      <span className="text-sm font-bold text-slate-900">
                        {(byPlan['professional'] || 0).toLocaleString()} credits
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-success-600 h-2 rounded-full"
                        style={{ width: `${totalCredits > 0 ? ((byPlan['professional'] || 0) / totalCredits) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Basic</span>
                      <span className="text-sm font-bold text-slate-900">
                        {(byPlan['basic'] || 0).toLocaleString()} credits
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-info-600 h-2 rounded-full"
                        style={{ width: `${totalCredits > 0 ? ((byPlan['basic'] || 0) / totalCredits) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export Reports */}
            <Card>
              <CardHeader
                title="Export Reports"
                description="Download detailed reports as CSV files"
              />
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Credit Usage Report</h3>
                        <p className="text-sm text-slate-500">Credit balances and thresholds</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleExport('credit_usage')}
                      isLoading={isExporting}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV
                    </Button>
                  </div>

                  <div className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Activity className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">API Usage Report</h3>
                        <p className="text-sm text-slate-500">API calls and credit consumption</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleExport('api_usage')}
                      isLoading={isExporting}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Low Credit Customers */}
            {lowCreditCustomers.length > 0 && (
              <Card>
                <CardHeader
                  title="Low Credit Accounts"
                  description={`${lowCreditCustomers.length} customers below threshold`}
                />
                <CardContent>
                  <div className="space-y-2">
                    {lowCreditCustomers.map((company) => (
                      <div
                        key={company.id}
                        className="flex items-center justify-between p-3 bg-warning-50 border border-warning-200 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{company.name}</p>
                          <p className="text-sm text-slate-500">{company.domain}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-warning-700">
                            {company.credits} credits
                          </p>
                          <p className="text-xs text-slate-500">
                            Threshold: {company.settings?.lowCreditThreshold || 100}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Internal Accounts */}
            {internalCompanies.length > 0 && (
              <Card>
                <CardHeader
                  title="Internal Accounts"
                  description={`${internalCompanies.length} internal account${internalCompanies.length !== 1 ? 's' : ''} with unlimited credits`}
                />
                <CardContent>
                  <div className="space-y-2">
                    {internalCompanies.map((company) => (
                      <div
                        key={company.id}
                        className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{company.name}</p>
                          <p className="text-sm text-slate-500">{company.domain}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-purple-700">
                            ∞ Unlimited
                          </p>
                          <p className="text-xs text-purple-600">
                            Internal Account
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
